import { Box, Flex, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import type { Bilhete } from "../../utils/interfaces";
import type { BilhetePagamento } from "../../hooks/pagamento/usePedido";

type CountdownState = {
  isExpired: boolean;
  remainingSeconds: number;
};

function parseExpiration(value?: string | null): Date | null {
  if (!value) return null;

  const trimmed = value.trim();
  const brDate = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/);

  if (brDate) {
    const [, day, month, year, hour, minute, second] = brDate;
    const date = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    );

    return Number.isNaN(date.getTime()) ? null : date;
  }

  const normalized = trimmed.includes("T") ? trimmed : trimmed.replace(" ", "T");
  const date = new Date(normalized);

  return Number.isNaN(date.getTime()) ? null : date;
}

function getBilhetes(bilhetes?: BilhetePagamento): Bilhete[] {
  if (!bilhetes) return [];

  return Object.values(bilhetes)
    .flatMap((agrupamentos) => Array.isArray(agrupamentos) ? agrupamentos : [])
    .flatMap((agrupamento) => Array.isArray(agrupamento.viagens) ? agrupamento.viagens : [])
    .flatMap((viagem) => Array.isArray(viagem.bilhetes) ? viagem.bilhetes : []);
}

function formatTime(totalSeconds: number): string {
  const safeSeconds = Math.max(totalSeconds, 0);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function getReservaDeadline(bilhetes?: BilhetePagamento): Date | null {
  const deadlines = getBilhetes(bilhetes)
    .map((bilhete) => parseExpiration(bilhete.expires_at))
    .filter((deadline): deadline is Date => deadline !== null)
    .sort((a, b) => a.getTime() - b.getTime());

  return deadlines[0] ?? null;
}

export function useReservaCountdown(deadline: Date | null): CountdownState {
  const deadlineTime = deadline?.getTime() ?? null;

  const calculateRemainingSeconds = () => {
    if (deadlineTime === null) return 0;

    return Math.max(Math.ceil((deadlineTime - Date.now()) / 1000), 0);
  };

  const [countdownState, setCountdownState] = useState(() => ({
    deadlineTime,
    remainingSeconds: calculateRemainingSeconds(),
  }));

  useEffect(() => {
    setCountdownState({
      deadlineTime,
      remainingSeconds: calculateRemainingSeconds(),
    });

    if (deadlineTime === null) return;

    const interval = window.setInterval(() => {
      setCountdownState({
        deadlineTime,
        remainingSeconds: calculateRemainingSeconds(),
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [deadlineTime]);

  const remainingSeconds = countdownState.deadlineTime === deadlineTime
    ? countdownState.remainingSeconds
    : calculateRemainingSeconds();

  return {
    isExpired: deadline !== null && remainingSeconds <= 0,
    remainingSeconds,
  };
}

interface ReservaCountdownProps {
  deadline: Date | null;
  remainingSeconds: number;
  isExpired: boolean;
}

export function ReservaCountdown({
  deadline,
  remainingSeconds,
  isExpired,
}: ReservaCountdownProps) {
  if (!deadline) return null;

  const isWarning = !isExpired && remainingSeconds <= 180;
  const accentColor = isExpired || isWarning ? "#F24A49" : "#530773";
  const timerBg = isExpired || isWarning ? "#F24A49" : "#530773";
  const softBg = isExpired || isWarning ? "#fff1f1" : "#fbf7ff";
  const borderColor = isExpired || isWarning ? "#ffd0d0" : "#e8d7f0";

  return (
    <Flex
      width={{ base: "100%", md: "min(100%, 34rem)" }}
      alignItems={"center"}
      justifyContent={"space-between"}
      flexDirection={{ base: "column", md: "row" }}
      gap={{ base: 2.5, md: 4 }}
      px={{ base: 3, md: 4 }}
      py={{ base: 2.5, md: 2.5 }}
      borderRadius={"md"}
      color={"#1f2430"}
      bg={softBg}
      border={"1px solid"}
      borderColor={borderColor}
      borderLeft={"5px solid"}
      borderLeftColor={accentColor}
      boxShadow={"0 6px 14px rgba(83, 7, 115, 0.08)"}
    >
      <Flex alignItems={"center"} gap={3} flex={1} width={"100%"}>
        <Box
          width={"10px"}
          height={"10px"}
          borderRadius={"full"}
          bg={accentColor}
          flexShrink={0}
          boxShadow={`0 0 0 4px ${isExpired || isWarning ? "rgba(242, 74, 73, 0.12)" : "rgba(83, 7, 115, 0.12)"}`}
        />
        <Box>
          <Text fontSize={{ base: "sm", md: "md" }} fontWeight={"800"} lineHeight={"1.2"} color={"#171923"}>
            {isExpired
              ? "Reserva expirada"
              : "Prazo para pagamento da reserva"}
          </Text>
          <Text fontSize={{ base: "xs", md: "sm" }} color={"#4a5568"} mt={0.5}>
            {isExpired
              ? "Gere uma nova reserva para continuar."
              : "O prazo usa a primeira reserva que vencer no pedido."}
          </Text>
        </Box>
      </Flex>
      <Box
        bg={timerBg}
        color={"white"}
        borderRadius={"md"}
        px={{ base: 4, md: 5 }}
        py={{ base: 1.5, md: 2 }}
        textAlign={"center"}
        minW={{ base: "100%", md: "7.5rem" }}
        boxShadow={"inset 0 -10px 18px rgba(0,0,0,0.12)"}
      >
        <Text fontSize={"9px"} textTransform={"uppercase"} letterSpacing={"0.13em"} opacity={0.86} fontWeight={"700"}>
          Restante
        </Text>
        <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight={"900"} lineHeight={"1"}>
          {formatTime(remainingSeconds)}
        </Text>
      </Box>
    </Flex>
  );
}
